import { Button } from '@mui/material';
import {
  GridColumnMenu,
  GridColumnsManagement,
  GridPanelWrapper,
  type DataGridProProps,
  type GridColDef,
  type GridColumnMenuProps,
  type GridColumnsPanelProps,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SeparatedDataGrid } from '~/components/data-grid';
import { useReportColumnVisibility } from '../lib';

// Column header menu trimmed to just the show/hide ("Manage columns") entry —
// sorting still works via header click, and reordering/pinning stay disabled.
const ColumnMenu = (props: GridColumnMenuProps) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuSortItem: null,
      columnMenuFilterItem: null,
      columnMenuPinningItem: null,
    }}
  />
);

export interface ReportDataGridProps<T extends GridValidRowModel> extends DataGridProProps<T> {
  // localStorage key suffix; each table passes a distinct value so column
  // show/hide choices persist independently per table.
  columnStateKey: string;
}

export const ReportDataGrid = <T extends GridValidRowModel>({
  columnStateKey,
  slots,
  ...props
}: ReportDataGridProps<T>) => {
  const { t } = useTranslation();
  const { columnVisibility, onColumnVisibilityChange, resetPreferences, hasPersistedPreferences } =
    useReportColumnVisibility(columnStateKey);

  const getTogglableColumns = useCallback(
    (cols: GridColDef[]) => cols.filter((col) => col.hideable !== false).map((col) => col.field),
    [],
  );

  const mergedSlots = useMemo(
    () => ({
      ...slots,
      columnMenu: ColumnMenu,
      columnsPanel: (panelProps: GridColumnsPanelProps) => (
        <GridPanelWrapper {...panelProps}>
          <GridColumnsManagement disableResetButton getTogglableColumns={getTogglableColumns} />
          <Button
            disabled={!hasPersistedPreferences}
            onClick={resetPreferences}
            size="small"
            sx={{ alignSelf: 'flex-end', m: 0.5, mt: '-35px' }}
          >
            {t('common.buttons.reset')}
          </Button>
        </GridPanelWrapper>
      ),
    }),
    [slots, getTogglableColumns, hasPersistedPreferences, resetPreferences, t],
  );

  return (
    <SeparatedDataGrid<T>
      {...props}
      columnVisibilityModel={columnVisibility}
      onColumnVisibilityModelChange={onColumnVisibilityChange}
      disableColumnMenu={false}
      slots={mergedSlots}
    />
  );
};
